import { z } from "zod";
import { instantSchema, localDateSchema } from "./schemas";

/*
 * Runtime schemas of rostering (T-176, ADR-0146; rosters, shifts and plan notices T-177, ADR-0148). They mirror `backend/openapi.json` like
 * `schemas.ts`.
 */

/** A local wall-clock time as the API sends it (`HH:mm:ss`). */
export const localTimeSchema = z.string().regex(/^\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/u);

/** A weekday as the API names it. */
export const weekdaySchema = z.enum([
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]);

/** `GET` and `PUT /tenants/{id}/rostering`. */
export const tenantRosteringSchema = z.object({
  enabled: z.boolean(),
  entitled: z.boolean(),
  active: z.boolean(),
  leadTimeDays: z.number().int(),
  colleagueNamesVisible: z.boolean(),
  swapAutoApproval: z.boolean(),
  planActualPerPersonVisible: z.boolean(),
  planActualMinimumGroupSize: z.number().int(),
  minLeadTimeDays: z.number().int(),
  maxLeadTimeDays: z.number().int(),
  changedAt: instantSchema.nullable(),
});

/** A grant of the right „Dienstplan erstellen“ (`PlanRoster`). */
export const rosterPlannerSchema = z.object({
  id: z.uuid(),
  employeeId: z.uuid(),
  displayName: z.string().nullable(),
  establishmentId: z.uuid(),
  teamId: z.uuid().nullable(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
});

/** `GET /roster-planners`. */
export const rosterPlannersSchema = z.array(rosterPlannerSchema);

/** `DELETE /roster-planners/{id}`. */
export const rosterPlannerRevocationSchema = z.object({
  id: z.uuid(),
  removed: z.boolean(),
  validTo: localDateSchema.nullable(),
});

/** An establishment the person sees in rostering. */
export const planningEstablishmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  timeZoneId: z.string(),
  canPlan: z.boolean(),
});

/** A team the person plans for at an establishment. */
export const planningTeamSchema = z.object({
  establishmentId: z.uuid(),
  teamId: z.uuid(),
  name: z.string(),
});

/** `GET /me/rostering`. */
export const myRosteringSchema = z.object({
  active: z.boolean(),
  plans: z.boolean(),
  isHr: z.boolean(),
  managesTenantTemplates: z.boolean(),
  grants: z.array(rosterPlannerSchema),
  establishments: z.array(planningEstablishmentSchema),
  teams: z.array(planningTeamSchema),
});

/** A shift template. */
export const shiftTemplateSchema = z.object({
  id: z.uuid(),
  establishmentId: z.uuid().nullable(),
  name: z.string(),
  startLocalTime: localTimeSchema,
  endLocalTime: localTimeSchema,
  endsNextDay: z.boolean(),
  plannedBreakMinutes: z.number().int(),
  validFrom: localDateSchema,
  validTo: localDateSchema.nullable(),
});

/** `GET /shift-templates`. */
export const shiftTemplatesSchema = z.array(shiftTemplateSchema);

/** A staffing requirement. */
export const staffingRequirementSchema = z.object({
  id: z.uuid(),
  establishmentId: z.uuid(),
  teamId: z.uuid().nullable(),
  weekday: weekdaySchema.nullable(),
  date: localDateSchema.nullable(),
  startLocalTime: localTimeSchema,
  endLocalTime: localTimeSchema,
  endsNextDay: z.boolean(),
  minimumStaff: z.number().int(),
});

/** `GET /staffing-requirements`. */
export const staffingRequirementsSchema = z.array(staffingRequirementSchema);

/** A roster (T-177). */
export const rosterSchema = z.object({
  id: z.uuid(),
  establishmentId: z.uuid(),
  teamId: z.uuid().nullable(),
  periodStart: localDateSchema,
  periodEnd: localDateSchema,
  status: z.enum(["Draft", "Published"]),
  version: z.number().int(),
});

/** `GET /rosters`. */
export const rostersSchema = z.array(rosterSchema);

/** A planned shift with its local times in the time zone of the establishment. */
export const plannedShiftSchema = z.object({
  id: z.uuid(),
  rosterId: z.uuid(),
  employeeId: z.uuid().nullable(),
  start: instantSchema,
  end: instantSchema,
  date: localDateSchema,
  startLocalTime: localTimeSchema,
  endLocalTime: localTimeSchema,
  endsNextDay: z.boolean(),
  plannedBreakMinutes: z.number().int(),
  shiftTemplateId: z.uuid().nullable(),
  note: z.string().nullable(),
  version: z.number().int(),
});

/** A notice of the plan check on a cell; never a violation of the recording. */
export const planNoticeSchema = z.object({
  employeeId: z.uuid().nullable(),
  date: localDateSchema,
  ruleId: z.string(),
  check: z.string(),
  severity: z.enum(["Breach", "Attention"]),
  legalReference: z.string().nullable(),
  actualValue: z.number().nullable(),
  limitValue: z.number().nullable(),
  unit: z.enum(["Seconds", "Days", "Count"]).nullable(),
  shiftId: z.uuid().nullable(),
  requirementId: z.uuid().nullable(),
});

/** A row of the planning view. */
export const rosterPersonSchema = z.object({
  employeeId: z.uuid(),
  displayName: z.string(),
});

/** `GET /rosters/{id}`. */
export const rosterPlanSchema = z.object({
  roster: rosterSchema,
  establishmentName: z.string(),
  timeZoneId: z.string(),
  persons: z.array(rosterPersonSchema),
  shifts: z.array(plannedShiftSchema),
  notices: z.array(planNoticeSchema),
});

/** A change of a shift with the incremental check of the persons concerned. */
export const rosterShiftChangeSchema = z.object({
  shift: plannedShiftSchema.nullable(),
  checkedPersons: z.array(z.uuid()),
  notices: z.array(planNoticeSchema),
});

/**
 * `POST /rosters/{id}/copy-week`; the planning view is loaded again afterwards. `skipped` counts shifts
 * whose person or template does not fit the new day, `alreadyPlanned` those the target week already
 * holds (F-111).
 */
export const rosterWeekCopySchema = z.object({
  copied: z.number().int(),
  skipped: z.number().int(),
  alreadyPlanned: z.number().int(),
});

/** One entry of the change list of a publication (T-178). */
export const rosterChangeSchema = z.object({
  kind: z.enum(["Added", "Changed", "Removed"]),
  shiftId: z.uuid(),
  shift: plannedShiftSchema.nullable(),
  /** The shift belongs to a person the planner does not see (not an open shift). */
  shiftPersonHidden: z.boolean(),
  previous: plannedShiftSchema.nullable(),
  previousPersonHidden: z.boolean(),
});

/** A changed shift that begins within the lead time (a notice, never a lock; O-36). */
export const shortNoticeShiftSchema = z.object({
  shiftId: z.uuid(),
  employeeId: z.uuid().nullable(),
  personHidden: z.boolean(),
  date: localDateSchema,
  daysAhead: z.number().int(),
});

/** `GET /rosters/{id}/publication`: what publishing the draft would do. */
export const rosterPublicationPreviewSchema = z.object({
  roster: rosterSchema,
  timeZoneId: z.string(),
  /** Fingerprint of the plan shown; the publication names it. */
  fingerprint: z.string(),
  changes: z.array(rosterChangeSchema),
  notifiedPersons: z.number().int(),
  noticeCount: z.number().int(),
  breachCount: z.number().int(),
  reasonRequired: z.boolean(),
  shortNotice: z.array(shortNoticeShiftSchema),
  leadTimeDays: z.number().int(),
});

/** A published version of a roster. */
export const rosterVersionSchema = z.object({
  version: z.number().int(),
  publishedAt: instantSchema,
  publishedByEmployeeId: z.uuid().nullable(),
  publishedByName: z.string().nullable(),
  reasonGiven: z.boolean(),
  reason: z.string().nullable(),
  addedShifts: z.number().int(),
  changedShifts: z.number().int(),
  removedShifts: z.number().int(),
  affectedPersons: z.number().int(),
  noticeCount: z.number().int(),
  shortNoticeCount: z.number().int(),
  leadTimeDays: z.number().int(),
});

/** `GET /rosters/{id}/versions`, newest first. */
export const rosterVersionsSchema = z.array(rosterVersionSchema);

/** `POST /rosters/{id}/publish`. */
export const rosterPublicationSchema = z.object({
  roster: rosterSchema,
  publication: rosterVersionSchema,
  notifiedPersons: z.number().int(),
  shortNotice: z.array(shortNoticeShiftSchema),
});

/** A published shift of „Mein Dienstplan“. */
export const myRosterShiftSchema = z.object({
  kind: z.enum(["Own", "Open", "Colleague"]),
  id: z.uuid(),
  establishmentId: z.uuid(),
  colleagueName: z.string().nullable(),
  start: instantSchema,
  end: instantSchema,
  date: localDateSchema,
  startLocalTime: localTimeSchema,
  endLocalTime: localTimeSchema,
  endsNextDay: z.boolean(),
  plannedBreakMinutes: z.number().int(),
  note: z.string().nullable(),
  publishedAt: instantSchema,
});

/** An establishment of „Mein Dienstplan“. */
export const myRosterEstablishmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  timeZoneId: z.string(),
});

/** `GET /me/roster` (T-178). */
export const myRosterSchema = z.object({
  generatedAt: instantSchema,
  from: localDateSchema,
  to: localDateSchema,
  colleagueNamesVisible: z.boolean(),
  establishments: z.array(myRosterEstablishmentSchema),
  shifts: z.array(myRosterShiftSchema),
  /** Days on which the own model takes its target from the roster (T-180); kept caches of older versions lack it. */
  targetFromRoster: z.array(z.object({ from: localDateSchema, to: localDateSchema })).default([]),
});

/** A roster. */
export type Roster = z.infer<typeof rosterSchema>;
/** A planned shift. */
export type PlannedShift = z.infer<typeof plannedShiftSchema>;
/** A notice of the plan check. */
export type PlanNotice = z.infer<typeof planNoticeSchema>;
/** A row of the planning view. */
export type RosterPerson = z.infer<typeof rosterPersonSchema>;
/** The planning view of a roster. */
export type RosterPlan = z.infer<typeof rosterPlanSchema>;
/** A change of a shift. */
export type RosterShiftChange = z.infer<typeof rosterShiftChangeSchema>;
/** The result of copying a week. */
export type RosterWeekCopy = z.infer<typeof rosterWeekCopySchema>;
/** One entry of the change list of a publication. */
export type RosterChange = z.infer<typeof rosterChangeSchema>;
/** A changed shift within the lead time. */
export type ShortNoticeShift = z.infer<typeof shortNoticeShiftSchema>;
/** What publishing the draft would do. */
export type RosterPublicationPreview = z.infer<typeof rosterPublicationPreviewSchema>;
/** A published version of a roster. */
export type RosterVersion = z.infer<typeof rosterVersionSchema>;
/** The result of a publication. */
export type RosterPublication = z.infer<typeof rosterPublicationSchema>;
/** A published shift of „Mein Dienstplan“. */
export type MyRosterShift = z.infer<typeof myRosterShiftSchema>;
/** „Mein Dienstplan“. */
export type MyRoster = z.infer<typeof myRosterSchema>;
/** Rostering as the tenant administrator sees it. */
export type TenantRostering = z.infer<typeof tenantRosteringSchema>;
/** A grant of `PlanRoster`. */
export type RosterPlanner = z.infer<typeof rosterPlannerSchema>;
/** An establishment the person sees in rostering. */
export type PlanningEstablishment = z.infer<typeof planningEstablishmentSchema>;
/** A team the person plans for. */
export type PlanningTeam = z.infer<typeof planningTeamSchema>;
/** Rostering as the person sees it. */
export type MyRostering = z.infer<typeof myRosteringSchema>;
/** A shift template. */
export type ShiftTemplate = z.infer<typeof shiftTemplateSchema>;
/** A staffing requirement. */
export type StaffingRequirement = z.infer<typeof staffingRequirementSchema>;
/** A weekday. */
export type Weekday = z.infer<typeof weekdaySchema>;

/** Query keys of rostering. */
export const rosterQueryKeys = {
  mine: ["me", "rostering"] as const,
  tenant: (tenantId: string) => ["tenant", tenantId, "rostering"] as const,
  templates: ["rostering", "shift-templates"] as const,
  requirements: ["rostering", "staffing-requirements"] as const,
  planners: ["rostering", "planners"] as const,
  rosters: (establishmentId: string, from: string, to: string) =>
    ["rostering", "rosters", establishmentId, from, to] as const,
  plan: (rosterId: string) => ["rostering", "roster", rosterId] as const,
  publication: (rosterId: string) => ["rostering", "roster", rosterId, "publication"] as const,
  versions: (rosterId: string) => ["rostering", "roster", rosterId, "versions"] as const,
  myRoster: (owner: string) => ["me", "roster", owner] as const,
};
